import { getCourseContent, calculateCourseProgress } from '../../../utils/progressUtils';

/**
 * Utility functions for progress calculations in the Student Dashboard
 */

/**
 * Get list of completed course IDs
 * @param {Array} pathCourses - Array of courses in the path
 * @param {Object} userProgress - User's progress data
 * @returns {Array} Array of completed course IDs
 */
export const getCompletedCourses = (pathCourses, userProgress) => {
  if (!pathCourses || !pathCourses.length) return [];

  const completedCourses = [];

  for (const course of pathCourses) {
    const courseContent = getCourseContent(course);
    // Exclude reinforcements from total count (they are optional interstitial content)
    const navigableContent = courseContent.filter(item => item.type !== 'reinforcement');
    const totalContent = navigableContent.length;

    if (totalContent === 0) continue; // Skip courses with no content

    let completedContent = 0;

    // Check completed lessons
    if (userProgress?.completedLessons) {
      const completedLessonIds = userProgress.completedLessons.map(cl => {
        try {
          if (typeof cl.lesson === 'object' && cl.lesson && cl.lesson._id) {
            return cl.lesson._id; // Old structure
          }
          return cl.lesson; // New structure (just the lesson ID)
        } catch (error) {
          return null;
        }
      }).filter(id => id !== null);

      navigableContent.forEach(item => {
        if (item.type === 'lesson' && completedLessonIds.includes(item._id)) {
          completedContent++;
        }
      });
    }

    // Check completed submission forms
    if (userProgress?.completedSubmissionForms) {
      const completedFormIds = userProgress.completedSubmissionForms.map(cf => {
        try {
          if (typeof cf.submissionForm === 'object' && cf.submissionForm && cf.submissionForm._id) {
            return cf.submissionForm._id; // Old structure
          }
          return cf.submissionForm; // New structure (just the form ID)
        } catch (error) {
          return null;
        }
      }).filter(id => id !== null);

      navigableContent.forEach(item => {
        if (item.type === 'submissionForm' && completedFormIds.includes(item._id)) {
          completedContent++;
        }
      });
    }

    // A course is completed if all its content (lessons + submission forms) are completed
    const isCompleted = completedContent === totalContent && totalContent > 0;

    if (isCompleted) {
      completedCourses.push(course._id);
    }
  }

  return completedCourses;
};

/**
 * Check if a specific course is completed
 * @param {string} courseId - Course ID to check
 * @param {Array} pathCourses - Array of courses in the path
 * @param {Object} userProgress - User's progress data
 * @returns {boolean} True if course is completed
 */
export const isCourseCompleted = (courseId, pathCourses, userProgress) => {
  if (!courseId || !pathCourses || !userProgress) return false;
  const completedCourses = getCompletedCourses(pathCourses, userProgress);
  return completedCourses.includes(courseId);
};

/**
 * Get completed content count for a course
 * @param {string} courseId - Course ID
 * @param {Array} pathCourses - Array of courses in the path
 * @param {Object} userProgress - User's progress data
 * @returns {number} Number of completed content items
 */
export const getCompletedContentForCourse = (courseId, pathCourses, userProgress) => {
  if (!courseId || !pathCourses || !userProgress) return 0;

  const course = pathCourses.find(c => c._id === courseId);
  if (!course) return 0;

  const progress = calculateCourseProgress(course, userProgress);
  return progress.completed;
};

/**
 * Get progress percentage for a specific course
 * @param {string} courseId - Course ID
 * @param {Array} pathCourses - Array of courses in the path
 * @param {Object} userProgress - User's progress data
 * @returns {number} Progress percentage (0-100)
 */
export const getCourseProgress = (courseId, pathCourses, userProgress) => {
  if (!courseId || !pathCourses || !userProgress) return 0;

  // When path is completed, all courses should show 100%
  if (userProgress?.isPathCompleted) {
    return 100;
  }

  const course = pathCourses.find(c => c._id === courseId);
  if (!course) return 0;

  const progress = calculateCourseProgress(course, userProgress);
  return progress.percentage;
};

/**
 * Calculate overall path progress
 * @param {Array} pathCourses - Array of courses in the path
 * @param {Object} userProgress - User's progress data
 * @param {Object} userCurrentCourse - Current course object
 * @returns {Object} Progress data { pathProgress, currentStep, courseProgress }
 */
export const calculatePathProgress = (pathCourses, userProgress, userCurrentCourse) => {
  if (!pathCourses || !pathCourses.length) return { pathProgress: 0, currentStep: 1, courseProgress: 0 };

  // Use backend-calculated progress when available
  if (userProgress?.pathProgress?.percentageComplete !== undefined) {
    const pathProgress = userProgress.pathProgress.percentageComplete;

    // Calculate current step (which course the student is on)
    let currentStep = 1;
    if (userCurrentCourse) {
      const currentCourseIndex = pathCourses.findIndex(course => course._id === userCurrentCourse._id);
      currentStep = currentCourseIndex >= 0 ? currentCourseIndex + 1 : 1;
    }

    // Calculate current course progress
    let courseProgress = 0;
    if (userCurrentCourse) {
      courseProgress = getCourseProgress(userCurrentCourse._id, pathCourses, userProgress);
    }

    return { pathProgress: Math.round(pathProgress), currentStep, courseProgress };
  }

  // Fallback calculation if no backend progress data
  const completedCourses = getCompletedCourses(pathCourses, userProgress);
  const totalCourses = pathCourses.length;

  let pathProgress = 0;
  if (userProgress?.isPathCompleted) {
    pathProgress = 100;
  } else {
    // Base progress from completed courses
    const baseProgress = (completedCourses.length / totalCourses) * 100;

    // Add current course progress if there's an active course
    if (userCurrentCourse) {
      const currentCourseProgress = getCourseProgress(userCurrentCourse._id, pathCourses, userProgress);
      const currentCourseWeight = (1 / totalCourses) * 100;
      const currentCourseContribution = (currentCourseProgress / 100) * currentCourseWeight;
      pathProgress = Math.min(100, baseProgress + currentCourseContribution);
    } else {
      pathProgress = baseProgress;
    }
  }

  // Calculate current step (which course the student is on)
  let currentStep;
  if (!userCurrentCourse) {
    currentStep = 1;
  } else {
    const currentCourseIndex = pathCourses.findIndex(course => course._id === userCurrentCourse._id);
    currentStep = currentCourseIndex + 1;
  }

  // Calculate current course progress
  let courseProgress = 0;
  if (userCurrentCourse) {
    courseProgress = getCourseProgress(userCurrentCourse._id, pathCourses, userProgress);
  }

  return { pathProgress: Math.round(pathProgress), currentStep, courseProgress };
};

/**
 * Calculate displayed course information
 * @param {Object} userCurrentCourse - Current course object
 * @param {Array} pathCourses - Array of courses in the path
 * @param {Object} userProgress - User's progress data
 * @param {number} courseProgress - Current course progress percentage
 * @param {number} currentStep - Current step in the path
 * @returns {Object} { displayedCourse, displayedCourseName }
 */
export const getDisplayedCourseInfo = (userCurrentCourse, pathCourses, userProgress, courseProgress, currentStep) => {
  let displayedCourse, displayedCourseName;

  if (!userCurrentCourse) {
    // No course assigned - show first course
    displayedCourse = pathCourses?.[0] || null;
    displayedCourseName = displayedCourse?.title || 'No Course Available';
  } else if (userProgress?.isPathCompleted) {
    // Path completed
    displayedCourse = userCurrentCourse;
    displayedCourseName = 'Goal Completed! 🎉';
  } else if (courseProgress === 100 && pathCourses && currentStep < pathCourses.length) {
    // Current course completed, show next course
    displayedCourse = pathCourses[currentStep] || userCurrentCourse; // currentStep is 0-indexed for next course
    displayedCourseName = `Next: ${displayedCourse?.title || 'Loading...'}`;
  } else {
    // User is in current course
    displayedCourse = userCurrentCourse;
    displayedCourseName = userCurrentCourse.title || 'Loading...';
  }

  return { displayedCourse, displayedCourseName };
};